import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import promoCodeService from "services/promoCode.services";
import getPlacesByProviderId from "services/getPlacesByProviderId";
import styles from "./styles.module.scss";
import { selectAuthToken, selectCurrentUser } from "store/auth/selectors";
import { FormButton } from "reusableComponents";
import { useTranslation } from "react-i18next";
import validatePromoCodeForm from "./validatePromoCodeForm";
import { getErrorMessage, clearError } from "helpers/errorHandling";
import { useModal } from "context";

// ------------------------------------------------------------
// Date helpers
// ------------------------------------------------------------
const toISODate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const formatDisplayDate = (iso, locale = "en") => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const getFirstWeekday = (year, month) => {
  const jsDay = new Date(year, month, 1).getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
};

// ------------------------------------------------------------
// Random promo code generator (used by the shuffle icon)
// ------------------------------------------------------------
const generateRandomCode = (length = 8) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // omit confusing chars
  let out = "";
  for (let i = 0; i < length; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return out;
};

// ------------------------------------------------------------
// Custom Date Picker
// ------------------------------------------------------------
const DatePicker = ({ value, onChange, error, label, required, i18n, t, name, hint }) => {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() =>
    value ? new Date(value) : new Date()
  );
  const wrapperRef = useRef(null);
  const locale = i18n?.language || "en";

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (value) setViewDate(new Date(value));
  }, [value]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const daysInMonth = getDaysInMonth(year, month);
  const firstWeekday = getFirstWeekday(year, month);

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const selectedISO = value ? toISODate(value) : "";
  const todayISO = toISODate(today);

  const weekdayKeys = [
    { key: "monday", fallback: "Mon" },
    { key: "tuesday", fallback: "Tue" },
    { key: "wednesday", fallback: "Wed" },
    { key: "thursday", fallback: "Thu" },
    { key: "friday", fallback: "Fri" },
    { key: "saturday", fallback: "Sat" },
    { key: "sunday", fallback: "Sun" },
  ];

  const monthLabel = new Date(year, month, 1).toLocaleString(locale, {
    month: "long",
  });

  return (
    <div className={styles.inputGroup} ref={wrapperRef}>
      <label htmlFor={name}>
        {label} {required && <i>*</i>}
      </label>

      <div
        className={`${styles.inputWrapper} ${styles.datePickerWrapper} ${
          error ? styles.inputError : ""
        }`}
        onClick={() => setOpen((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span
          className={`${styles.dateDisplay} ${
            !value ? styles.datePlaceholder : ""
          }`}
        >
          {value
            ? formatDisplayDate(value, locale)
            : t("dashboard.promoCodes.selectDatePlaceholder")}
        </span>

        <svg
          className={styles.dateIcon}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M3 9H21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M16 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </div>

      {error && <span className={styles.errorMessage}>{error}</span>}
      {!error && hint && <span className={styles.helperText}>{hint}</span>}

      {open && (
        <div className={styles.calendar} role="dialog">
          <div className={styles.calendarHeader}>
            <button
              type="button"
              className={styles.calendarNavBtn}
              onClick={() => setViewDate(new Date(year, month - 1, 1))}
              aria-label="Previous month"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <div className={styles.calendarTitle}>
              {monthLabel} {year}
            </div>

            <button
              type="button"
              className={styles.calendarNavBtn}
              onClick={() => setViewDate(new Date(year, month + 1, 1))}
              aria-label="Next month"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className={styles.calendarGrid}>
            {weekdayKeys.map(({ key, fallback }) => (
              <div key={key} className={styles.weekdayCell}>
                {t(`days.short.${key}`, fallback)}
              </div>
            ))}

            {cells.map((day, idx) => {
              if (!day)
                return <div key={`empty-${idx}`} className={styles.emptyCell} />;

              const iso = toISODate(day);
              const isSelected = iso === selectedISO;
              const isToday = iso === todayISO;
              const isPast = day < today;

              return (
                <button
                  key={iso}
                  type="button"
                  disabled={isPast}
                  onClick={() => {
                    onChange(iso);
                    setOpen(false);
                  }}
                  className={`${styles.dayCell} ${
                    isSelected ? styles.dayCellSelected : ""
                  } ${isToday && !isSelected ? styles.dayCellToday : ""} ${
                    isPast ? styles.dayCellDisabled : ""
                  }`}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------
// Main component
// ------------------------------------------------------------
const PromoCode = () => {
  const { t, i18n } = useTranslation("global");
  const { openModal } = useModal();
  const navigate = useNavigate();
  const { promoCodeId } = useParams();

  const accessToken = useSelector(selectAuthToken);
  const currentUser = useSelector(selectCurrentUser);

  const [promoCodeData, setPromoCodeData] = useState(null);
  const [places, setPlaces] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    percentage: "",
    // start_date: "",        // commented out — kept for reference
    expiration_date: "",
    customer_email: "",
    customer_name: "",
    description: "",          // kept — backend expects it (may be required)
    one_time_use: false,
    places: [],
  });

  // --- Fetch provider's places ---
  useEffect(() => {
    const providerId = currentUser?.simplified_id;
    if (!providerId) return;

    const fetchPlaces = async () => {
      setPlacesLoading(true);
      try {
        const data = await getPlacesByProviderId(providerId);
        setPlaces(data || []);
      } catch (err) {
        setPlaces([]);
      } finally {
        setPlacesLoading(false);
      }
    };
    fetchPlaces();
  }, [currentUser?.simplified_id]);

  // --- Fetch existing promo code (edit mode) ---
  useEffect(() => {
    if (!promoCodeId) return;
    const fetchPromoCode = async () => {
      setIsLoading(true);
      try {
        const data = await promoCodeService.get(promoCodeId, accessToken);
        setPromoCodeData(data.data);
      } catch (error) {
        console.error("Error fetching promo code:", error);
        setPromoCodeData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPromoCode();
  }, [promoCodeId, accessToken]);

  // --- Hydrate form from promoCodeData ---
  useEffect(() => {
    if (promoCodeData && promoCodeId) {
      const normalizedPlaces = Array.isArray(promoCodeData.places)
        ? promoCodeData.places
            .map((p) => (typeof p === "string" ? p : p?.id || p?.uuid || null))
            .filter(Boolean)
        : [];

      setFormData({
        title: promoCodeData.title || "",
        percentage: promoCodeData.percentage || "",
        // start_date: promoCodeData.start_date
        //   ? toISODate(promoCodeData.start_date)
        //   : "",   // commented out
        expiration_date: promoCodeData.expiration_date
          ? toISODate(promoCodeData.expiration_date)
          : "",
        customer_email: promoCodeData.customer_email || "",
        customer_name: promoCodeData.customer_name || "",
        description: promoCodeData.description || "",   // kept
        one_time_use: promoCodeData.one_time_use || false,
        places: normalizedPlaces,
      });
    }
  }, [promoCodeData, promoCodeId]);

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => clearError(name, prev));
  };

  // const handleStartDateChange = (iso) => {
  //   setFormData((prev) => ({ ...prev, start_date: iso }));
  //   setErrors((prev) => clearError("start_date", prev));
  // }; // commented out

  const handleExpirationDateChange = (iso) => {
    setFormData((prev) => ({ ...prev, expiration_date: iso }));
    setErrors((prev) => clearError("expiration_date", prev));
  };

  const handleGenerateCode = () => {
    const randomCode = generateRandomCode(8);
    setFormData((prev) => ({ ...prev, title: randomCode }));
    setErrors((prev) => clearError("title", prev));
  };

  const togglePlace = (uuid) => {
    setFormData((prev) => ({
      ...prev,
      places: prev.places.includes(uuid)
        ? prev.places.filter((id) => id !== uuid)
        : [...prev.places, uuid],
    }));
    setErrors((prev) => clearError("places", prev));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validatePromoCodeForm(formData, errors, setErrors, t, !!promoCodeId)) return;

    setIsLoading(true);

    const data = {
      ...formData,
      // start_date: formData.start_date, // commented out
      expiration_date: formData.expiration_date,
    };

    try {
      const response = promoCodeId
        ? await promoCodeService.update(promoCodeData.id, data, accessToken)
        : await promoCodeService.create(data, accessToken);

      if (response.success) {
        const successMessage = promoCodeId
          ? t("dashboard.promoCodes.updateSuccess")
          : t("dashboard.promoCodes.createSuccess");

        openModal({
          content: <p>{successMessage}</p>,
          title: null,
          type: "success",
          autoClose: true,
          closeDelay: 1000,
        });
        setTimeout(() => navigate("/dashboard/promo-codes"), 1500);
      }
    } catch (error) {
      console.error("Error saving promo code:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Text input renderer ---
  const renderTextInput = (name, label, opts = {}) => {
    const {
      type = "text",
      required = false,
      disabled = false,
      placeholder,
      hint,
      maxLength,
      pattern,
      autoComplete,
    } = opts;
    const fieldError = getErrorMessage(name, errors);

    return (
      <div className={styles.inputGroup}>
        <label htmlFor={name}>
          {label} {required && <i>*</i>}
        </label>
        <div
          className={`${styles.inputWrapper} ${fieldError ? styles.inputError : ""}`}
        >
          <input
            id={name}
            name={name}
            type={type}
            value={formData[name] ?? ""}
            onChange={handleChange}
            placeholder={placeholder || ""}
            className={styles.textInput}
            disabled={disabled}
            maxLength={maxLength}
            pattern={pattern}
            autoComplete={autoComplete}
          />
        </div>
        {fieldError ? (
          <span className={styles.errorMessage}>{fieldError}</span>
        ) : hint ? (
          <span className={styles.helperText}>{hint}</span>
        ) : null}
      </div>
    );
  };

  return (
    <div className={`${styles.pageContainer} ${isLoading ? styles.loading : ""}`}>
      <form onSubmit={onSubmit} className={styles.formContainer} noValidate>
        {/* ---------- Card header with title ---------- */}
        <div className={styles.formHeader}>
          <h2 className={styles.formTitle}>
            {promoCodeId
              ? t("dashboard.promoCodes.editTitle")
              : t("dashboard.promoCodes.createCouponTitle")}
          </h2>
        </div>

        {promoCodeId && !promoCodeData ? (
          <p className={styles.errorMessage}>
            {t("dashboard.promoCodes.noSuchPromoCodeFound")}
          </p>
        ) : (
          <>
            <p className={styles.formSubtitle}>
              {t("dashboard.promoCodes.codeHint")}
            </p>

            <div className={styles.formBody}>
              {/* 1. Company / beneficiary name */}
              {renderTextInput(
                "customer_name",
                t("dashboard.promoCodes.companyOrBeneficiary"),
                { required: true, autoComplete: "off" }
              )}

              {/* 2. Promo code — with shuffle icon */}
              <div className={styles.inputGroup}>
                <label htmlFor="title">
                  {t("dashboard.promoCodes.code")} <i>*</i>
                </label>
                <div
                  className={`${styles.inputWrapper} ${styles.codeInputWrapper} ${
                    getErrorMessage("title", errors) ? styles.inputError : ""
                  }`}
                >
                  <input
                    id="title"
                    name="title"
                    type="text"
                    value={formData.title ?? ""}
                    onChange={handleChange}
                    placeholder=""
                    className={styles.textInput}
                    disabled={!!promoCodeData?.id}
                    maxLength={8}
                    pattern="[A-Za-z0-9]{1,8}"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className={styles.shuffleBtn}
                    onClick={handleGenerateCode}
                    aria-label={t("dashboard.promoCodes.generateCode")}
                    title={t("dashboard.promoCodes.generateCode")}
                    disabled={!!promoCodeData?.id}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M16 3h5v5M21 3l-7.5 7.5M8 21H3v-5M3 21l7.5-7.5M21 16v5h-5M14 14l7 7M3 8V3h5M10 10L3 3"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
                {getErrorMessage("title", errors) ? (
                  <span className={styles.errorMessage}>
                    {getErrorMessage("title", errors)}
                  </span>
                ) : (
                  <span className={styles.helperText}>
                    {t("dashboard.promoCodes.codeHelper")}
                  </span>
                )}
              </div>

              {/* 3. Discount amount */}
              {renderTextInput("percentage", t("dashboard.promoCodes.discountAmount"), {
                type: "number",
                required: true,
                hint: t("dashboard.promoCodes.percentHelper"),
              })}

              {/* 4. Dates row — start (commented out) + end */}
              <div className={styles.datesRow}>
                {/* start date — commented out */}
                {/* <DatePicker
                  name="start_date"
                  label={t("dashboard.promoCodes.startDate")}
                  value={formData.start_date}
                  onChange={handleStartDateChange}
                  error={getErrorMessage("start_date", errors)}
                  i18n={i18n}
                  t={t}
                /> */}

                <DatePicker
                  name="expiration_date"
                  label={t("dashboard.promoCodes.endDate")}
                  required
                  value={formData.expiration_date}
                  onChange={handleExpirationDateChange}
                  error={getErrorMessage("expiration_date", errors)}
                  i18n={i18n}
                  t={t}
                />
              </div>

              {/* 5. One-time use */}
              <div className={styles.checkboxWrapper}>
                <input
                  type="checkbox"
                  id="one_time_use"
                  name="one_time_use"
                  checked={formData.one_time_use}
                  onChange={handleChange}
                />
                <label htmlFor="one_time_use" className={styles.checkboxLabel}>
                  {t("dashboard.promoCodes.validOnce")}
                </label>
              </div>

              {/* 6. Beneficiary email */}
              {renderTextInput(
                "customer_email",
                t("dashboard.promoCodes.beneficiaryEmail"),
                {
                  type: "email",
                  required: true,
                  disabled: !!promoCodeData?.id,
                  autoComplete: "off",
                }
              )}

              {/* 7. Applied spaces — horizontal list */}
              <div className={styles.spacesSection}>
                <div className={styles.spacesHeader}>
                  <h3 className={styles.sectionTitle}>
                    {t("dashboard.promoCodes.validForSpaces")} <i>*</i>
                  </h3>
                </div>

                {placesLoading ? (
                  <div className={styles.placesList}>
                    {[1, 2].map((i) => (
                      <div key={i} className={styles.placeRowSkeleton}>
                        <div className={styles.skeletonThumb} />
                        <div className={styles.skeletonBody}>
                          <div className={styles.skeletonLine} />
                          <div className={styles.skeletonLineShort} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : places.length === 0 ? (
                  <p className={styles.placesEmpty}>
                    {t("dashboard.promoCodes.noPlacesAvailable")}
                  </p>
                ) : (
                  <div className={styles.placesList}>
                    {places.map((place) => {
                      const selected = formData.places.includes(place.uuid);
                      const image =
                        place.images?.[0]?.thumb_image_url ||
                        place.images?.[0]?.image_url;

                      const addressLine = [
                        place.address?.address,
                        place.address?.city,
                        place.address?.country,
                      ]
                        .filter(Boolean)
                        .join(", ");

                      return (
                        <label
                          key={place.uuid}
                          className={`${styles.placeRow} ${
                            selected ? styles.placeRowSelected : ""
                          }`}
                        >
                          <div className={styles.placeThumbWrapper}>
                            {image ? (
                              <img
                                src={image}
                                alt={place.name}
                                className={styles.placeThumb}
                                loading="lazy"
                              />
                            ) : (
                              <div className={styles.placeThumbPlaceholder}>
                                {place.name?.charAt(0)?.toUpperCase() || "?"}
                              </div>
                            )}
                          </div>

                          <div className={styles.placeInfoCol}>
                            <div className={styles.placeNameRow}>
                              <span className={styles.placeName}>
                                {place.name}
                              </span>
                              {place.placeUsage?.name && (
                                <span className={styles.placeUsageBadge}>
                                  {place.placeUsage.name}
                                </span>
                              )}
                            </div>

                            {addressLine && (
                              <span className={styles.placeAddress}>
                                {addressLine}
                              </span>
                            )}
                            {place.address?.city && (
                              <span className={styles.placeCity}>
                                {place.address.city}
                              </span>
                            )}
                          </div>

                          <input
                            type="checkbox"
                            className={styles.placeCheckbox}
                            checked={selected}
                            onChange={() => togglePlace(place.uuid)}
                          />
                        </label>
                      );
                    })}
                  </div>
                )}

                {getErrorMessage("places", errors) && (
                  <span className={styles.errorMessage}>
                    {getErrorMessage("places", errors)}
                  </span>
                )}
              </div>

              {/* 8. Submit — full width */}
              <div className={styles.submitButtonWrapper}>
                <FormButton
                  type="submit"
                  label={
                    promoCodeId
                      ? t("dashboard.promoCodes.save")
                      : t("dashboard.promoCodes.createCouponCta")
                  }
                  variant="primary"
                  isLoading={isLoading}
                />
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default PromoCode;


// import { useState, useEffect } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { useSelector } from "react-redux";
// import promoCodeService from "services/promoCode.services";
// import styles from "./styles.module.scss";
// import { selectAuthToken } from "store/auth/selectors";
// import {
//   Checkbox,
//   CustomInput,
//   CustomTextarea,
//   FormButton,
//   CustomDatePicker,
// } from "reusableComponents";
// import { useTranslation } from "react-i18next";
// import validatePromoCodeForm from "./validatePromoCodeForm";
// import { getErrorMessage } from "helpers/errorHandling";
// import { useModal } from "context";

// const PromoCode = () => {
//   const { t } = useTranslation("global");
//   const { openModal } = useModal();
//   const navigate = useNavigate();
//   const { promoCodeId } = useParams();
//   const [promoCodeData, setPromoCodeData] = useState(null);
//   const [formData, setFormData] = useState({
//     title: "",
//     percentage: "",
//     expiration_date: "",
//     customer_email: "",
//     description: "",
//     one_time_use: false,
//   });
//   const accessToken = useSelector(selectAuthToken);
//   const [errors, setErrors] = useState({});
//   const [isLoading, setIsLoading] = useState(false);

//   useEffect(() => {
//     if (promoCodeId) {
//       const fetchPromoCode = async () => {
//         setIsLoading(true);
//         try {
//           const data = await promoCodeService.get(promoCodeId, accessToken);
//           setPromoCodeData(data.data);
//         } catch (error) {
//           console.error("Error fetching promo code:", error);
//           setPromoCodeData(null);
//         } finally {
//           setIsLoading(false);
//         }
//       };
//       fetchPromoCode();
//     }
//   }, [promoCodeId, accessToken]);

//   useEffect(() => {
//     if (promoCodeData && promoCodeId) {
//       setFormData({
//         title: promoCodeData.title || "",
//         percentage: promoCodeData.percentage || "",
//         expiration_date: promoCodeData.expiration_date
//           ? new Date(promoCodeData.expiration_date).toISOString().split("T")[0]
//           : "",
//         customer_email: promoCodeData.customer_email || "",
//         description: promoCodeData.description || "",
//         one_time_use: promoCodeData.one_time_use || false,
//       });
//     }
//   }, [promoCodeData]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData((prevState) => ({
//       ...prevState,
//       [name]: type === "checkbox" ? checked : value,
//     }));
//   };

//   const onSubmit = async (e) => {
//     setIsLoading(true);
//     e.preventDefault();
//     if (!validatePromoCodeForm(formData, errors, setErrors, t)) return;

//     const data = {
//       ...formData,
//       expiration_date: formatDate(formData.expiration_date),
//     };
//     try {
//       const response = promoCodeId
//         ? await promoCodeService.update(promoCodeData.id, data, accessToken)
//         : await promoCodeService.create(data, accessToken);
//       if (response.success) {
//         openModal({
//           content: <p>{response.message}</p>,
//           title: null,
//           type: "success",
//           autoClose: true,
//           closeDelay: 1000,
//         });
//         setTimeout(() => {
//           navigate("/dashboard/promo-codes");
//         }, 1500);
//       }
//     } catch (error) {
//       console.error("Error saving promo code:", error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const formatDate = (date) => {
//     const d = new Date(date);
//     return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
//       2,
//       "0"
//     )}-${String(d.getDate()).padStart(2, "0")}`;
//   };

//   return (
//     <div className={`${styles.container} ${isLoading ? styles.loading : ""}`}>
//       <h2 className={styles.dashboardTitle}>
//         {promoCodeId ? "Edit Promo Code" : "Create Promo Code"}
//       </h2>
//       {promoCodeId && !promoCodeData ? (
//         <p className={styles.errorMessage}>
//           {t("dashboard.noSuchPromoCodeFound")}
//         </p>
//       ) : (
//         <form onSubmit={onSubmit} className={styles.form} noValidate>
//           <CustomInput
//             required
//             type="text"
//             name="title"
//             value={formData.title}
//             handleChange={handleChange}
//             label={t("dashboard.promoCode")}
//             disabled={!!promoCodeData?.id}
//             error={getErrorMessage("title", errors)} // Use getErrorMessage to retrieve the error
//           />

//           <CustomInput
//             required
//             type="number"
//             name="percentage" // Corrected name
//             value={formData.percentage}
//             handleChange={handleChange}
//             label={t("dashboard.percent")}
//             error={getErrorMessage("percentage", errors)} // Use getErrorMessage to retrieve the error
//           />

//           <CustomDatePicker
//             required
//             grayLabel={false}
//             labelAbove={false}
//             styled
//             selectedDate={formData.expiration_date}
//             onDateChange={(date) =>
//               setFormData((prevState) => ({
//                 ...prevState,
//                 expiration_date: date,
//               }))
//             }
//             label={t("dashboard.expirationDate")}
//             error={getErrorMessage("expiration_date", errors)} // Use getErrorMessage to retrieve the error
//           />

//           <CustomInput
//             required
//             type="email"
//             name="customer_email"
//             value={formData.customer_email}
//             handleChange={handleChange}
//             label={t("dashboard.customerEmail")}
//             error={getErrorMessage("customer_email", errors)} // Use getErrorMessage to retrieve the error
//             disabled={!!promoCodeData?.id}
//           />

//           <CustomTextarea
//             required
//             name="description"
//             value={formData.description}
//             onChange={handleChange}
//             label={t("dashboard.description")}
//             error={getErrorMessage("description", errors)} // Use getErrorMessage to retrieve the error
//           />

//           <Checkbox
//             name="one_time_use"
//             checked={formData.one_time_use}
//             onChange={handleChange}
//             label={t("dashboard.oneTimeUse")}
//             isSquare
//             iconType
//           />

//           <FormButton
//             type="submit"
//             label={
//               promoCodeId ? t("buttons.save") : t("dashboard.promoCodes.create")
//             }
//           />
//         </form>
//       )}
//     </div>
//   );
// };

// export default PromoCode;
